import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useParams, useLocation, useHistory } from 'react-router-dom';
import {
  Box, IconButton, Typography, Button, Grid, Avatar, Tooltip, Link
} from '@mui/material';
import { DeleteOutlined } from '@mui/icons-material';
import { MRTTable } from '../../../components';
import {
  ToastV3
} from '../components';
import { useToastContext } from '../../../hooks';
import { SMPAPI } from '../../../fetch/smp';
import { ILicense, IUser } from '../../../models/smp/';
import { AddEditLicenseModal } from './include/modals';
import { ConfirmDeleteModal } from '../components';

interface RouteParams {
  applicationId?: string;
}

const ApplicationLicenses: React.FC = () => {
  const { applicationId } = useParams<RouteParams>();
  const location = useLocation();
  const history = useHistory();
  const { showToast } = useToastContext();

  const showToastRef = useRef(showToast);
  useEffect(() => { showToastRef.current = showToast }, [showToast]);

  const [licenses, setLicenses] = useState<ILicense[]>([]);
  const [application, setApplication] = useState<any>(null);
  const [logoUrl, setLogoUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [editingLicense, setEditingLicense] = useState<ILicense | undefined>();
  const [modalOpen, setModalOpen] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  // States for confirm delete modal
  const [pendingDeleteLicense, setPendingDeleteLicense] = useState<ILicense | null>(null);
  const [assignedUsers, setAssignedUsers] = useState<IUser[]>([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [loadingDependencies, setLoadingDependencies] = useState(false);
  const [reloadCounter, setReloadCounter] = useState(0);

  // MRT states
  const [mrtSorting, setMrtSorting] = useState<any[]>([]);
  const [mrtColumnFilters, setMrtColumnFilters] = useState<any[]>([]);
  const [mrtGlobalFilter, setMrtGlobalFilter] = useState<string>('');
  const mrtSearchDebounceRef = useRef<any>(null);

  // Cleanup search debounce timeout on unmount
  useEffect(() => {
    return () => {
      if (mrtSearchDebounceRef.current) {
        clearTimeout(mrtSearchDebounceRef.current);
      }
    };
  }, []);

  // Persist MRT state (sorting/filters/page) in localStorage
  const storageKey = React.useMemo(
    () => `smp:licenses:mrt:${applicationId || 'all'}`,
    [applicationId]
  );

  // Restore persisted MRT state safely
  useEffect(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) {
        const s = JSON.parse(raw);
        if (Array.isArray(s?.sorting)) setMrtSorting(s.sorting);
        if (Array.isArray(s?.columnFilters)) setMrtColumnFilters(s.columnFilters);
        if (typeof s?.globalFilter === 'string') setMrtGlobalFilter(s.globalFilter);
        if (typeof s?.page === 'number' && s.page >= 0) setPage(s.page);
        if (typeof s?.perPage === 'number' && s.perPage > 0) setPerPage(s.perPage);
      }
    } catch {}
  }, [storageKey]);

  useEffect(() => {
    const s = {
      sorting: mrtSorting,
      columnFilters: mrtColumnFilters,
      globalFilter: mrtGlobalFilter,
      page,
      perPage,
    };
    try { localStorage.setItem(storageKey, JSON.stringify(s)); } catch {}
  }, [storageKey, mrtSorting, mrtColumnFilters, mrtGlobalFilter, page, perPage]);

  // Load application info
  useEffect(() => {
    if (!applicationId) return;
    (async () => {
      try {
        const app = await SMPAPI.getApplication(applicationId);
        setApplication(app);
        const fileId = SMPAPI.getFileIdFromLogoPath(app.LogoPath);
        if (fileId) {
          setLogoUrl(await SMPAPI.getApplicationLogoUrl(fileId));
        }
      } catch {
        showToastRef.current('error', 'Failed to load application details');
      }
    })();
  }, [applicationId]);

  // Reset page when filters change
  useEffect(() => {
    setPage(0);
  }, [applicationId]);

  // Build server query params from current UI/MRT state
  const buildLicenseQueryParams = useCallback((opts?: { page?: number; perPage?: number }) => {
    // Build server-side sort param from MRT sorting state for global sorting across pages
    const allowedSortIds = new Set([
      'licenseName',
      'licenseType', 
      'licenseStatus',
      'startDate',
      'endDate',
      'autoRenew',
      'cancelByDate',
      'renewByDate',
      'annualCost',
      'seatQuantity',
      'creditQuantity',
      'totalValue',
      'monthlyContractValue',
      'approvalNotes',
      'otherNotes',
    ]);
    const sortString = (Array.isArray(mrtSorting) ? mrtSorting : [])
      .map((s: any) => {
        const id = s.id || s.columnId || s.accessorKey;
        if (!id || !allowedSortIds.has(id)) return null;
        const dir = s.desc ? 'desc' : 'asc';
        return `${id}:${dir}`;
      })
      .filter(Boolean)
      .join(',');

    const pageIdx = opts?.page ?? (page + 1);
    const pageSize = opts?.perPage ?? perPage;
    
    // Build search from column filters and global filter
    const columnSearchTerms = (Array.isArray(mrtColumnFilters) ? mrtColumnFilters : [])
      .map((filter: any) => filter.value)
      .filter(Boolean);
    const globalSearchTerm = mrtGlobalFilter ? [mrtGlobalFilter] : [];
    const allSearchTerms = [...columnSearchTerms, ...globalSearchTerm];
    const searchString = allSearchTerms.length > 0 ? allSearchTerms.join(' ') : undefined;
    
    return {
      page: pageIdx,
      perPage: pageSize,
      search: searchString,
      sort: sortString || undefined,
    };
  }, [mrtSorting, mrtColumnFilters, mrtGlobalFilter, page, perPage]);

  const loadLicenses = useCallback(async () => {
    if (!applicationId) return;
    setLoading(true);
    try {
      const params = buildLicenseQueryParams();
      const res = await SMPAPI.fetchLicenses(applicationId, params);
      const startIndex = page * perPage;
      setLicenses(
        res.records.map((rec, i) => ({
          ...rec,
          rowCount: startIndex + i + 1,
        }))
      );
      setTotal(res.totalRecordCount);
    } catch (e) {
      showToastRef.current('error', 'Failed to load licenses');
    } finally {
      setLoading(false);
    }
  }, [applicationId, buildLicenseQueryParams, page, perPage]);

  // Load licenses when page changes, reloadCounter bumps
  useEffect(() => {
    loadLicenses();
  }, [loadLicenses, reloadCounter, page]);

  // Reload when MRT state changes (sorting, filtering)
  useEffect(() => {
    loadLicenses();
  }, [loadLicenses, mrtSorting, mrtColumnFilters, mrtGlobalFilter]);

  // Open license modal from URL query param
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const licenseIdParam = searchParams.get('licenseId');
    if (!licenseIdParam) return;
    
    const licenseId = parseInt(licenseIdParam, 10);
    if (isNaN(licenseId)) return;
    
    (async () => {
      try {
        const lic = await SMPAPI.getLicense(licenseId);
        setEditingLicense(lic);
        setModalOpen(true);
        searchParams.delete('licenseId');
        history.replace({
          pathname: location.pathname,
          search: searchParams.toString() || '',
        });
      } catch {
        showToastRef.current('error', 'Failed to load license details');
      }
    })();
  }, [location.search, history, location.pathname]);

  // Reload assigned users for the pending delete license
  const reloadAssignedUsers = useCallback(async () => {
    if (!pendingDeleteLicense || !applicationId) return;
    setLoadingDependencies(true);
    try {
      const userRes = await SMPAPI.fetchUsers(applicationId, { page: 1, perPage: 999 });
      const assigned = userRes.records.filter(u => u.assignedLicense === pendingDeleteLicense.licenseID);
      setAssignedUsers(assigned);
    } catch {
      showToast('error', 'Failed to load assigned users');
    } finally {
      setLoadingDependencies(false);
    }
  }, [pendingDeleteLicense, applicationId, showToast]);

  // Request delete for a license
  const requestDelete = useCallback(async (license: ILicense) => {
    if (!license.licenseID) {
      showToast('error', 'Invalid license ID');
      return;
    }
    setPendingDeleteLicense(license);
    setLoadingDependencies(true);
    try {
      const userRes = await SMPAPI.fetchUsers(applicationId!, { page: 1, perPage: 999 });
      const assigned = userRes.records.filter(u => {
        const userLicenseId = u.assignedLicense ? String(u.assignedLicense) : null;
        const licenseId = String(license.licenseID);
        return userLicenseId === licenseId;
      });
    
      setAssignedUsers(assigned);
      setLoadingDependencies(false);
      if (assigned.length > 0) {
        setShowDeleteModal(true);
      } else {
        setPendingDeleteId(license.licenseID);
        setShowDeleteConfirm(true);
      }
    } catch (error: any) {
      showToast('error', error?.detail || 'Failed to load assigned users');
      setLoadingDependencies(false);
    }
  }, [applicationId, showToast]);

  // Confirm delete for simple deletion (no dependencies)
  const confirmDelete = useCallback(async () => {
    if (!pendingDeleteId) return;
    try {
      await SMPAPI.deleteLicense(pendingDeleteId);
      showToast('success', 'License deleted');
      setShowDeleteConfirm(false);
      setPendingDeleteId(null);
      setReloadCounter(c => c + 1);
    } catch {
      showToast('error', 'Failed to delete license');
    }
  }, [pendingDeleteId, showToast]);

  const cancelDelete = useCallback(() => {
    setShowDeleteConfirm(false);
    setPendingDeleteId(null);
  }, []);

  const handleSave = useCallback(async (values: ILicense) => {
    try {
      await SMPAPI.upsertLicense(values);
      showToast('success', values.licenseID ? 'License updated' : 'License created');
      setModalOpen(false);
      setEditingLicense(undefined);
      setReloadCounter(c => c + 1);
    } catch (e: any) {
      showToast('error', e?.detail ?? 'Failed to save license');
    }
  }, [showToast]);

  // User columns for the modal
  const userColumns = useMemo(() => [
    { Header: 'User Name', accessor: 'userName' },
    { Header: 'Email', accessor: 'email' },
    { Header: 'Employment Status', accessor: 'employmentStatus' },
  ], []);

  // MRT columns (memoized) - avoid remounts that collapse the search field
  const mrtColumns = useMemo(() => {
    const unique = (arr: any[], key: string) => Array.from(new Set(arr.map(x => (x?.[key] ?? '').toString()).filter(Boolean))).sort();
    const licenseTypeOptions = unique(licenses, 'licenseType');
    const licenseStatusOptions = unique(licenses, 'licenseStatus');

    const ellipsisCell = (text: any, maxWidth: number = 250) => (
      <Tooltip title={text ?? ''} arrow>
        <Box component="span" sx={{ display: 'inline-block', maxWidth, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', verticalAlign: 'middle', lineHeight: '1.5' }}>{text ?? ''}</Box>
      </Tooltip>
    );

    const currencyCell = (val: any) => {
      const formatted = typeof val === 'number' || (typeof val === 'string' && val !== '')
        ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 }).format(Number(val))
        : '';
      return ellipsisCell(formatted, 120);
    };

    return [
      {
        accessorKey: 'rowCount',
        header: '#',
        size: 30,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ row }: any) => row.original.rowCount,
      },
      {
        accessorKey: 'licenseName',
        header: 'Name',
        filterVariant: 'text',
        size: 300,
        muiTableBodyCellProps: { sx: { maxWidth: 300, verticalAlign: 'middle', lineHeight: '1.5' } },
        Cell: ({ row }: any) => (
          <Tooltip title={row.original.licenseName} arrow>
            <Link
              component="button"
              onClick={async () => {
                if (row.original?.licenseID) {
                  const lic = await SMPAPI.getLicense(row.original.licenseID);
                  setEditingLicense(lic);
                  setModalOpen(true);
                }
              }}
              sx={{ textDecoration: 'underline', fontWeight: 500, color: '#1976d2', cursor: 'pointer', fontSize: 'inherit', verticalAlign: 'middle', lineHeight: '1.5' }}
            >
              {row.original.licenseName}
            </Link>
          </Tooltip>
        ),
      },
      { accessorKey: 'licenseType', header: 'License Type', filterVariant: 'select', filterSelectOptions: licenseTypeOptions, size: 140, Cell: ({ cell }: any) => ellipsisCell(cell.getValue(), 140) },
      { accessorKey: 'licenseStatus', header: 'License Status', filterVariant: 'select', filterSelectOptions: licenseStatusOptions, size: 120, Cell: ({ cell }: any) => ellipsisCell(cell.getValue(), 120) },
      { accessorKey: 'startDate', header: 'Start Date', filterVariant: 'text', size: 100, Cell: ({ cell }: any) => ellipsisCell(cell.getValue(), 100) },
      { accessorKey: 'endDate', header: 'End Date', filterVariant: 'text', size: 100, Cell: ({ cell }: any) => ellipsisCell(cell.getValue(), 100) },
      { accessorKey: 'autoRenew', header: 'Auto Renew', filterVariant: 'select', filterSelectOptions: ['Y', 'N'], size: 100, Cell: ({ cell }: any) => cell.getValue() === 'Y' ? 'Yes' : 'No' },
      { accessorKey: 'cancelByDate', header: 'Cancel By Date', filterVariant: 'text', size: 120, Cell: ({ cell }: any) => ellipsisCell(cell.getValue(), 120) },
      { accessorKey: 'renewByDate', header: 'Renew By Date', filterVariant: 'text', size: 120, Cell: ({ cell }: any) => ellipsisCell(cell.getValue(), 120) },
      { accessorKey: 'annualCost', header: 'Annual Cost Per User/Credit/Add-On', filterVariant: 'text', size: 160, Cell: ({ cell }: any) => currencyCell(cell.getValue()) },
      { accessorKey: 'seatQuantity', header: 'Seat Quantity', filterVariant: 'text', size: 100, Cell: ({ cell }: any) => ellipsisCell(cell.getValue(), 100) },
      { accessorKey: 'creditQuantity', header: 'Credit Quantity', filterVariant: 'text', size: 100, Cell: ({ cell }: any) => ellipsisCell(cell.getValue(), 100) },
      { accessorKey: 'totalValue', header: 'Total Contract Value', filterVariant: 'text', size: 140, Cell: ({ cell }: any) => currencyCell(cell.getValue()) },
      { accessorKey: 'monthlyContractValue', header: 'Monthly Contract Value', filterVariant: 'text', size: 140, Cell: ({ cell }: any) => currencyCell(cell.getValue()) },
      { accessorKey: 'approvalNotes', header: 'Approval Notes', filterVariant: 'text', size: 150, enableSorting: false, Cell: ({ cell }: any) => {
        const v = (cell.getValue?.() ?? '').toString();
        const text = v.trim();
        return ellipsisCell(text, 150);
      } },
      { accessorKey: 'otherNotes', header: 'Other Notes', filterVariant: 'text', size: 150, enableSorting: false, Cell: ({ cell }: any) => {
        const v = (cell.getValue?.() ?? '').toString();
        const text = v.trim();
        return ellipsisCell(text, 150);
      } },
      { id: 'actions', header: 'Delete', size: 80, enableColumnFilter: false, Cell: ({ row }: any) => {
        const license = row.original;
        if (!license.licenseID) return <Typography variant="caption" sx={{ lineHeight: '1.5' }}>N/A</Typography>;
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: '1.5' }}>
            <IconButton size="small" color="error" onClick={() => requestDelete(license)} sx={{ p: 0 }}>
              <DeleteOutlined fontSize="small" />
            </IconButton>
          </Box>
        );
      } },
    ];
  }, [licenses, requestDelete]);




  return (
    <>
      <Box mb={2} sx={{ bgcolor: '#f0f0f0', p: 2, borderRadius: 2, overflow: 'hidden' }}>
        <Grid container spacing={1} alignItems="start" mb={2}>
          <Grid
            item
            xs={12}
            sm={6}
            container
            alignItems="center"
            spacing={2}
            sx={{
              '@media (max-width: 1150px)': {
                flexDirection: 'column', 
                alignItems: 'stretch',
              },
            }}
          >
            <Grid item>
              <Avatar
                variant="square"
                src={logoUrl}
                alt={application?.name || 'Application'}
                sx={{
                  bgcolor: '#ccc',
                  borderRadius: 1,
                  objectFit: 'cover',
                  height: 40,
                  width: 'auto',
                }}
              />
            </Grid>
            <Grid item>
              <Typography variant="h6">Application</Typography>
              <Typography variant="body2" color="textSecondary">
                {application?.name}
              </Typography>
            </Grid>
          </Grid>

          <Grid item xs={6} textAlign="right">
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={() => {
                setEditingLicense({});
                setModalOpen(true);
              }}
            >
              Add Contract / Subscription
            </Button>
          </Grid>
        </Grid>

        <Box
          sx={{
            maxWidth: '100%',
            overflowX: 'auto',
            position: 'relative',
            minHeight: 100,
          }}
        >
          <Box
            sx={{
              minWidth: '100%',
              overflow: 'auto',
              border: '1px solid #e0e0e0', 
              borderRadius: '8px',           
              background: 'white',          
              '& table': {
                width: '100%',
                tableLayout: 'auto',
              },
              '& .MuiTableCell-root': {
                fontSize: '0.75rem',
                padding: '6px 8px',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: '250px', 
                borderBottom: '1px solid rgb(212, 210, 210)', 
              },
              '& .MuiTableCell-head': {
                fontWeight: 'bold',
                whiteSpace: 'normal',
                overflowWrap: 'normal',
                wordWrap: 'normal',
                wordBreak: 'keep-all',
                hyphens: 'manual',
                maxWidth: '150px',
                transition: 'color 0.3s ease',
                borderBottom: '2px solid rgb(212, 210, 210)',
              },
              '& .MuiTableCell-head:hover, & .MuiTableCell-head:hover *': {
                color: '#64b5f6',
                cursor: 'pointer',
              },
              '& th:last-of-type, & td:last-of-type': {
                position: 'sticky',
                right: 0,
                zIndex: 2,
                textAlign: 'center ! important',
              },
              'td:last-of-type': {
                p: 0,
                backgroundColor: '#fff',
              },
              '& .MuiTableCell-body': { py: 0.1 },
              '&::-webkit-scrollbar': { height: 2 },
              '&::-webkit-scrollbar-track': { background: '#e0e0e0' },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: '#a0a0a0',
                borderRadius: 3,
              },
              scrollbarHeight: 'thin',
              scrollbarColor: '#fff #fff',
            }}
          >
            <MRTTable
              columns={mrtColumns as any}
              data={licenses as any}
              loading={loading}
              manualPagination
              manualSorting
              totalRowCount={total}
              pageIndex={page}
              pageSize={perPage}
              onPaginationChange={(updater: any) => {
                const next = typeof updater === 'function' ? updater({ pageIndex: page, pageSize: perPage }) : updater;
                if (typeof next?.pageIndex === 'number') setPage(next.pageIndex);
                if (typeof next?.pageSize === 'number') setPerPage(next.pageSize);
              }}
              onSortingChange={(updater: any) => {
                const next = typeof updater === 'function' ? updater(mrtSorting) : updater;
                setMrtSorting(next || []);
                // Reset to first page so server can repaginate after sort changes
                setPage(0);
              }}
              sorting={mrtSorting}
              columnFilters={mrtColumnFilters}
              onColumnFiltersChange={(updater: any) => {
                const next = typeof updater === 'function' ? updater(mrtColumnFilters) : updater;
                setMrtColumnFilters(next || []);
                // Reset to first page when filters change to search entire dataset
                setPage(0);
              }}
              globalFilter={mrtGlobalFilter}
              onGlobalFilterChange={(val: any) => {
                const v = typeof val === 'string' ? val : '';
                setMrtGlobalFilter(v);
                // Debounce the search to prevent too many API calls
                if (mrtSearchDebounceRef.current) {
                  clearTimeout(mrtSearchDebounceRef.current);
                }
                mrtSearchDebounceRef.current = setTimeout(() => {
                  if (v.trim().length >= 2 || v.trim().length === 0) {
                    // Reset to first page when searching to search entire dataset
                    setPage(0);
                  }
                }, 300);
              }}
            />
          </Box>
        </Box>
      </Box>
      <AddEditLicenseModal
        open={modalOpen}
        initialData={editingLicense}
        applicationID={Number(applicationId!)}
        onClose={() => {
          setModalOpen(false);
          setEditingLicense(undefined);
        }}
        onSave={async (formValues) => {
          const payload: ILicense = {
            ...formValues,
            startDate: formValues.startDate ? formValues.startDate.toISOString() : undefined,
            endDate: formValues.endDate ? formValues.endDate.toISOString() : undefined,
            cancelByDate: formValues.cancelByDate ? formValues.cancelByDate.toISOString() : undefined,
            renewByDate: formValues.renewByDate ? formValues.renewByDate.toISOString() : undefined,
            initialPaymentDate: formValues.initialPaymentDate ? formValues.initialPaymentDate.toISOString() : undefined,
          };
          await handleSave(payload);
        }}
      />
      {showDeleteConfirm && (
        <ToastV3
          type="error"
          showReset={false}
          message="Are you sure you want to delete this license?"
          confirmText="Delete"
          onCancel={cancelDelete}
          onSave={confirmDelete}
          onClose={cancelDelete}
        />
      )}
      {pendingDeleteLicense && (
        <ConfirmDeleteModal<IUser>
          open={showDeleteModal}
          title={`Cannot delete license: ${pendingDeleteLicense.licenseName}`}
          message="The following users are assigned to this license. Delete or unlink them first."
          columns={userColumns}
          data={assignedUsers}
          isLoading={loadingDependencies}
          onDeleteItem={async (user) => {
            await SMPAPI.deleteUser(user.applicationUserID!);
            await reloadAssignedUsers();
          }}
          onUnlinkItem={async (user) => {
            await SMPAPI.detachLicenseFromUser(user.applicationUserID!);
            await reloadAssignedUsers();
          }}
          onDeleteAllItems={async () => {
            await Promise.all(assignedUsers.map(u => SMPAPI.deleteUser(u.applicationUserID!)));
            await reloadAssignedUsers();
          }}
          onConfirm={async () => {
            await SMPAPI.deleteLicense(pendingDeleteLicense.licenseID!);
            setShowDeleteModal(false);
            setPendingDeleteLicense(null);
            setReloadCounter(c => c + 1);
          }}
          onClose={() => {
            setShowDeleteModal(false);
            setPendingDeleteLicense(null);
          }}
          confirmText="Delete License"
          deleteAllText="Delete All Assigned Users"
        />
      )}
    </>
  );
};

export default ApplicationLicenses;